import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Quiz Service
 * Handles all quiz-related Firestore operations
 */

/**
 * Creates a new quiz in Firestore
 * @param {Object} quizData - Quiz data (title, description, questions)
 * @returns {Promise<Object>} Created quiz with generated ID
 */
export const createQuiz = async (quizData) => {
  try {
    // Generate unique quiz ID
    const quizRef = doc(collection(db, 'quizzes'));
    const quizId = quizRef.id;

    // Auto-generate question and answer IDs
    const processedQuestions = quizData.questions.map((question, qIndex) => ({
      id: `q${qIndex + 1}`,
      type: question.type || 'quiz',
      text: question.text,
      imageUrl: question.imageUrl || null,
      answers: question.answers.map((answer, aIndex) => ({
        id: `a${aIndex + 1}`,
        text: answer.text,
        isCorrect: answer.isCorrect || false
      })),
      timeLimit: question.timeLimit || 20,
      points: question.points || 1000
    }));

    const quiz = {
      id: quizId,
      title: quizData.title,
      description: quizData.description || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      questions: processedQuestions
    };

    await setDoc(quizRef, quiz);

    return quiz;
  } catch (error) {
    console.error('Error creating quiz:', error);
    throw new Error(`Klarte ikkje opprette kviss: ${error.message}`);
  }
};

/**
 * Fetches a quiz by ID
 * @param {string} quizId - Quiz document ID
 * @returns {Promise<Object>} Quiz data
 */
export const getQuiz = async (quizId) => {
  try {
    const quizRef = doc(db, 'quizzes', quizId);
    const quizSnap = await getDoc(quizRef);

    if (!quizSnap.exists()) {
      throw new Error('Fann ikkje kvissen');
    }

    return quizSnap.data();
  } catch (error) {
    console.error('Error fetching quiz:', error);
    throw new Error(`Klarte ikkje hente kviss: ${error.message}`);
  }
};

/**
 * Updates an existing quiz
 * @param {string} quizId - Quiz document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateQuiz = async (quizId, updates) => {
  try {
    const quizRef = doc(db, 'quizzes', quizId);

    // Auto-generate question and answer IDs if questions are being updated
    if (updates.questions) {
      updates.questions = updates.questions.map((question, qIndex) => ({
        id: question.id || `q${qIndex + 1}`,
        type: question.type || 'quiz',
        text: question.text,
        imageUrl: question.imageUrl || null,
        answers: question.answers.map((answer, aIndex) => ({
          id: answer.id || `a${aIndex + 1}`,
          text: answer.text,
          isCorrect: answer.isCorrect || false
        })),
        timeLimit: question.timeLimit || 20,
        points: question.points || 1000
      }));
    }

    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };

    await updateDoc(quizRef, updatedData);

  } catch (error) {
    console.error('Error updating quiz:', error);
    throw new Error(`Klarte ikkje oppdatere kviss: ${error.message}`);
  }
};

/**
 * Deletes a quiz
 * @param {string} quizId - Quiz document ID
 * @returns {Promise<void>}
 */
export const deleteQuiz = async (quizId) => {
  try {
    const quizRef = doc(db, 'quizzes', quizId);
    await deleteDoc(quizRef);

  } catch (error) {
    console.error('Error deleting quiz:', error);
    throw new Error(`Klarte ikkje slette kviss: ${error.message}`);
  }
};

/**
 * Fetches all quizzes, ordered by most recently updated
 * @returns {Promise<Array>} Array of quiz objects
 */
export const getAllQuizzes = async () => {
  try {
    const quizzesRef = collection(db, 'quizzes');
    const q = query(quizzesRef, orderBy('updatedAt', 'desc'));
    const snapshot = await getDocs(q);

    const quizzes = [];
    snapshot.forEach((doc) => {
      quizzes.push({ id: doc.id, ...doc.data() });
    });

    return quizzes;
  } catch (error) {
    console.error('Error fetching all quizzes:', error);
    throw new Error(`Klarte ikkje hente kvissar: ${error.message}`);
  }
};

/**
 * Gets quiz statistics (total plays, active quizzes count)
 * @returns {Promise<Object>} Stats object with totalPlays and activeQuizzes
 */
export const getQuizStats = async () => {
  try {
    const quizzesRef = collection(db, 'quizzes');
    const snapshot = await getDocs(quizzesRef);

    let totalPlays = 0;
    let activeQuizzes = 0;

    snapshot.forEach((doc) => {
      const data = doc.data();
      totalPlays += data.playCount || 0;
      activeQuizzes++;
    });

    return { totalPlays, activeQuizzes };
  } catch (error) {
    console.error('Error fetching quiz stats:', error);
    return { totalPlays: 0, activeQuizzes: 0 };
  }
};

/**
 * Duplicates an existing quiz with a new ID and " (Kopi)" suffix
 * @param {string} quizId - ID of the quiz to duplicate
 * @returns {Promise<Object>} The newly created quiz copy
 */
export const duplicateQuiz = async (quizId) => {
  try {
    const original = await getQuiz(quizId);
    const copy = {
      title: original.title + ' (Kopi)',
      description: original.description || '',
      questions: original.questions,
    };
    return await createQuiz(copy);
  } catch (error) {
    console.error('Error duplicating quiz:', error);
    throw new Error(`Klarte ikkje kopiere kviss: ${error.message}`);
  }
};
